import { useMemo, useState, useEffect } from 'react';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { format, parseISO, startOfDay, isBefore } from 'date-fns';
import { Ban, CheckCircle2, Clock, User, Phone, Car } from 'lucide-react';
import { adminBlockSlot, adminUnblockSlot } from '@/lib/api';

const WEEKDAY_SLOTS = [
  { id: 'slot_m1', label: '8:00 AM - 11:00 AM' },
  { id: 'slot_m2', label: '11:00 AM - 1:00 PM' },
  { id: 'slot_m3', label: '1:30 PM - 5:00 PM' },
];
// Legacy slot IDs — kept for displaying/unblocking historical bookings.
const LEGACY_SLOTS = [
  { id: 'slot_fs1', label: '8:30 AM - 12:30 PM' },
  { id: 'slot_fs2', label: '1:00 PM - 4:00 PM' },
  { id: 'slot_fs3', label: '5:00 PM - 8:00 PM' },
  { id: 'slot_wt1', label: '6:30 PM - 9:30 PM (Quieter Cars)' },
];
const ALL_SLOTS = [...WEEKDAY_SLOTS, ...LEGACY_SLOTS];

function slotsForDate(date) {
  const day = date.getDay(); // 0 Sun ... 6 Sat
  if (day === 0) return []; // Sunday closed
  return WEEKDAY_SLOTS; // Mon–Sat: three bookable slots
}

/**
 * Interactive calendar for blocking days/slots.
 * - Red cell = manually blocked (any block on that day)
 * - Green ring = has one or more customer bookings
 * - Tap a day to open a dialog letting admin either block the entire day
 *   or pick specific time slots to block/unblock.
 */
export default function BlockCalendar({ blockedSlots, bookings, onChanged }) {
  const [openDate, setOpenDate] = useState(null); // Date or null
  const [scope, setScope] = useState('full_day'); // 'full_day' | 'specific'
  const [reason, setReason] = useState('');
  const [pendingSlotToggles, setPendingSlotToggles] = useState({}); // slot_id -> desired state (true=blocked/false=unblocked)
  const [saving, setSaving] = useState(false);

  // Index blocked entries by date
  const blockedByDate = useMemo(() => {
    const m = new Map();
    (blockedSlots || []).forEach((b) => {
      const arr = m.get(b.date) || [];
      arr.push(b);
      m.set(b.date, arr);
    });
    return m;
  }, [blockedSlots]);

  // Index bookings by date
  const bookingsByDate = useMemo(() => {
    const m = new Map();
    (bookings || []).forEach((b) => {
      if (b.status === 'cancelled') return;
      const arr = m.get(b.date) || [];
      arr.push(b);
      m.set(b.date, arr);
    });
    return m;
  }, [bookings]);

  // Dates that have any block record (full-day OR specific slot)
  const blockedDates = useMemo(
    () => Array.from(blockedByDate.keys()).map((d) => parseISO(d + 'T00:00:00')),
    [blockedByDate]
  );
  // Dates that have a full-day block (slot_id = null)
  const fullDayBlockedDates = useMemo(
    () =>
      Array.from(blockedByDate.entries())
        .filter(([_, arr]) => arr.some((b) => !b.slot_id))
        .map(([d]) => parseISO(d + 'T00:00:00')),
    [blockedByDate]
  );
  const bookedDates = useMemo(
    () => Array.from(bookingsByDate.keys()).map((d) => parseISO(d + 'T00:00:00')),
    [bookingsByDate]
  );

  const handleDayClick = (day) => {
    if (!day) return;
    if (isBefore(day, startOfDay(new Date()))) return;
    setOpenDate(day);
    // Default scope: if already fully blocked -> allow unblock-all via "full_day" preselected
    const dateStr = format(day, 'yyyy-MM-dd');
    const entries = blockedByDate.get(dateStr) || [];
    const isFullDay = entries.some((b) => !b.slot_id);
    setScope(isFullDay ? 'full_day' : 'specific');
    setReason(isFullDay ? entries.find((b) => !b.slot_id)?.reason || '' : '');
    setPendingSlotToggles({});
  };

  const closeDialog = () => {
    setOpenDate(null);
    setScope('full_day');
    setReason('');
    setPendingSlotToggles({});
  };

  useEffect(() => {
    // Reset local toggles when day changes
    setPendingSlotToggles({});
  }, [openDate]);

  if (!openDate && openDate !== null) return null;

  const dateStr = openDate ? format(openDate, 'yyyy-MM-dd') : '';
  const daySlots = openDate ? slotsForDate(openDate) : [];
  const dayBlocked = openDate ? blockedByDate.get(dateStr) || [] : [];
  const dayBookings = openDate ? bookingsByDate.get(dateStr) || [] : [];
  const fullDayBlock = dayBlocked.find((b) => !b.slot_id);
  const isFullyBlocked = !!fullDayBlock;

  const slotIsBlocked = (slotId) => dayBlocked.some((b) => b.slot_id === slotId);
  const slotBooking = (slotId) => dayBookings.find((b) => b.slot_id === slotId);
  const slotIsPendingToggle = (slotId) => pendingSlotToggles[slotId] !== undefined;
  const slotFinalBlockedState = (slotId) =>
    slotIsPendingToggle(slotId) ? pendingSlotToggles[slotId] : slotIsBlocked(slotId);

  const toggleSlot = (slotId) => {
    if (slotBooking(slotId)) return; // never let admin block a booked slot from here
    const currentlyBlocked = slotIsBlocked(slotId);
    setPendingSlotToggles((prev) => {
      const next = { ...prev };
      if (next[slotId] !== undefined) {
        // revert
        delete next[slotId];
      } else {
        next[slotId] = !currentlyBlocked;
      }
      return next;
    });
  };

  const applyChanges = async () => {
    if (!openDate) return;
    setSaving(true);
    try {
      if (scope === 'full_day') {
        if (isFullyBlocked) {
          // Unblock full-day
          await adminUnblockSlot(fullDayBlock.id);
          toast.success(`Unblocked ${format(openDate, 'EEE, d MMM')}`);
        } else {
          // Block full-day
          await adminBlockSlot({
            date: dateStr,
            slot_id: null,
            reason: reason || null,
          });
          toast.success(`Blocked ${format(openDate, 'EEE, d MMM')} (full day)`);
        }
      } else {
        // scope === 'specific'
        // If there's a full-day block, unblock it first so slot-level blocks make sense
        if (isFullyBlocked) {
          await adminUnblockSlot(fullDayBlock.id);
        }
        const toBlock = [];
        const toUnblock = [];
        Object.entries(pendingSlotToggles).forEach(([slotId, desired]) => {
          const already = slotIsBlocked(slotId);
          if (desired && !already) {
            toBlock.push(slotId);
          } else if (!desired && already) {
            const rec = dayBlocked.find((b) => b.slot_id === slotId);
            if (rec) toUnblock.push(rec.id);
          }
        });
        if (toBlock.length === 0 && toUnblock.length === 0 && !isFullyBlocked) {
          toast.info('No changes to apply');
          setSaving(false);
          return;
        }
        for (const slotId of toBlock) {
          await adminBlockSlot({
            date: dateStr,
            slot_id: slotId,
            reason: reason || null,
          });
        }
        for (const blockedId of toUnblock) {
          await adminUnblockSlot(blockedId);
        }
        toast.success(
          `Updated ${format(openDate, 'EEE, d MMM')} — ${toBlock.length} blocked, ${toUnblock.length} unblocked`
        );
      }
      closeDialog();
      if (onChanged) await onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="glass p-4 md:p-6 border border-zinc-800 bg-zinc-900/50" data-testid="block-calendar">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wide text-white">
            Tap to Block / Unblock
          </h3>
          <p className="text-zinc-400 text-sm">
            Tap any future day to block the whole day or pick specific time slots.
          </p>
        </div>
        <div className="flex items-center gap-4 text-xs text-zinc-400 flex-wrap">
          <span className="flex items-center gap-1.5">
            <span
              className="w-3 h-3 inline-block border border-red-500"
              style={{
                background:
                  'repeating-linear-gradient(45deg,#7f1d1d,#7f1d1d 3px,#450a0a 3px,#450a0a 6px)',
              }}
            />
            Manually Blocked
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 bg-transparent rounded-sm inline-block ring-2 ring-green-500" />
            Customer Booking
          </span>
        </div>
      </div>

      <div className="flex justify-center">
        <Calendar
          mode="single"
          onDayClick={handleDayClick}
          disabled={{ before: new Date() }}
          modifiers={{
            fullBlocked: fullDayBlockedDates,
            partialBlocked: blockedDates.filter(
              (d) => !fullDayBlockedDates.some((fd) => fd.getTime() === d.getTime())
            ),
            hasBookings: bookedDates,
          }}
          modifiersClassNames={{
            fullBlocked:
              'bg-red-900/70 text-red-100 hover:bg-red-800 border border-red-600 font-bold [background-image:repeating-linear-gradient(45deg,rgba(239,68,68,0.35)_0,rgba(239,68,68,0.35)_3px,transparent_3px,transparent_6px)]',
            partialBlocked:
              'bg-red-950/40 text-red-300 hover:bg-red-900/50 border border-red-800',
            hasBookings: 'ring-2 ring-green-500 ring-offset-1 ring-offset-zinc-900',
          }}
          className="text-white rounded-sm"
          data-testid="block-calendar-widget"
        />
      </div>

      {/* Day details dialog */}
      <Dialog open={!!openDate} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-xl font-bold uppercase text-white">
              {openDate && format(openDate, 'EEEE, d MMMM yyyy')}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {isFullyBlocked
                ? 'This whole day is currently blocked.'
                : dayBookings.length > 0
                ? `You have ${dayBookings.length} customer booking${dayBookings.length > 1 ? 's' : ''} on this day.`
                : 'Choose how much of this day to block.'}
            </DialogDescription>
          </DialogHeader>

          {/* Bookings summary — quick glance at who's booked */}
          {dayBookings.length > 0 && (
            <div className="mt-2 border border-green-800/60 bg-green-950/30" data-testid="day-bookings-summary">
              <div className="px-3 py-2 border-b border-green-800/60 text-green-300 text-xs uppercase tracking-wider font-['Barlow_Condensed']">
                Bookings on this day
              </div>
              <div className="divide-y divide-green-900/50">
                {dayBookings.map((b) => {
                  const slot = ALL_SLOTS.find((s) => s.id === b.slot_id);
                  const vehicle = [b.customer?.vehicle_year, b.customer?.vehicle_make, b.customer?.vehicle_model]
                    .filter(Boolean)
                    .join(' ');
                  return (
                    <div key={b.id} className="p-3 space-y-1" data-testid={`day-booking-${b.id}`}>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-2 min-w-0">
                          <User className="w-4 h-4 text-green-400 flex-shrink-0" />
                          <span className="text-white font-medium truncate">{b.customer?.name || 'Customer'}</span>
                        </div>
                        {slot && (
                          <span className="text-green-400 text-xs font-mono flex items-center gap-1 whitespace-nowrap">
                            <Clock className="w-3 h-3" />
                            {slot.label}
                          </span>
                        )}
                      </div>
                      {b.customer?.phone && (
                        <a
                          href={`tel:${b.customer.phone}`}
                          className="flex items-center gap-2 text-green-400 hover:text-green-300 text-sm"
                          data-testid={`day-booking-phone-${b.id}`}
                        >
                          <Phone className="w-3.5 h-3.5" />
                          <span className="font-mono">{b.customer.phone}</span>
                        </a>
                      )}
                      {vehicle && (
                        <div className="flex items-center gap-2 text-zinc-400 text-sm">
                          <Car className="w-3.5 h-3.5 flex-shrink-0" />
                          <span className="truncate">{vehicle}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Scope toggle */}
          <div className="grid grid-cols-2 gap-2 mt-2">
            <button
              type="button"
              onClick={() => setScope('full_day')}
              className={`p-3 border text-sm font-['Barlow_Condensed'] uppercase tracking-wide transition-colors ${
                scope === 'full_day'
                  ? 'border-red-500 bg-red-900/30 text-red-200'
                  : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'
              }`}
              data-testid="scope-full-day-btn"
            >
              <Ban className="w-4 h-4 inline mr-1 mb-0.5" />
              Full Day
            </button>
            <button
              type="button"
              onClick={() => setScope('specific')}
              disabled={daySlots.length === 0}
              className={`p-3 border text-sm font-['Barlow_Condensed'] uppercase tracking-wide transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                scope === 'specific'
                  ? 'border-red-500 bg-red-900/30 text-red-200'
                  : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'
              }`}
              data-testid="scope-specific-btn"
            >
              <Clock className="w-4 h-4 inline mr-1 mb-0.5" />
              Specific Slot(s)
            </button>
          </div>

          {scope === 'full_day' ? (
            <div className="space-y-3 pt-2">
              {!isFullyBlocked && (
                <div className="space-y-2">
                  <Label className="text-zinc-300 text-xs uppercase tracking-wider">Reason (optional)</Label>
                  <Input
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="bg-zinc-950 border-zinc-800"
                    placeholder="e.g., Sick, Holiday, Family time"
                    data-testid="block-day-reason-input"
                  />
                </div>
              )}
              {isFullyBlocked && fullDayBlock.reason && (
                <p className="text-zinc-400 text-sm">
                  Reason: <span className="text-zinc-200">{fullDayBlock.reason}</span>
                </p>
              )}
              {dayBookings.length > 0 && !isFullyBlocked && (
                <div className="p-3 border border-yellow-800 bg-yellow-900/20 text-yellow-200 text-sm">
                  ⚠️ Warning: you have {dayBookings.length} customer booking(s) on this day. Blocking the full day will hide new slots from customers but will not cancel existing bookings.
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2 pt-2">
              {daySlots.length === 0 ? (
                <p className="text-zinc-500 text-sm">There are no bookable slots on this day of the week.</p>
              ) : (
                <>
                  <div className="space-y-2">
                    {daySlots.map((slot) => {
                      const booking = slotBooking(slot.id);
                      const nowBlocked = slotFinalBlockedState(slot.id);
                      const disabled = !!booking;
                      const changed = slotIsPendingToggle(slot.id);
                      return (
                        <button
                          key={slot.id}
                          type="button"
                          onClick={() => toggleSlot(slot.id)}
                          disabled={disabled}
                          className={`w-full p-3 text-left border flex items-center justify-between transition-colors ${
                            disabled
                              ? 'border-green-800 bg-green-950/40 cursor-not-allowed'
                              : nowBlocked
                              ? 'border-red-600 bg-red-900/40 text-red-100'
                              : 'border-zinc-700 bg-zinc-950 text-zinc-200 hover:border-zinc-500'
                          }`}
                          data-testid={`slot-toggle-${slot.id}`}
                        >
                          <div className="flex items-center gap-3">
                            {disabled ? (
                              <User className="w-4 h-4 text-green-400" />
                            ) : nowBlocked ? (
                              <Ban className="w-4 h-4 text-red-300" />
                            ) : (
                              <CheckCircle2 className="w-4 h-4 text-zinc-500" />
                            )}
                            <div>
                              <p className="text-sm font-mono">{slot.label}</p>
                              {disabled && (
                                <p className="text-green-400 text-xs">
                                  Booked by {booking.customer?.name || 'a customer'}
                                </p>
                              )}
                              {!disabled && nowBlocked && (
                                <p className="text-red-300 text-xs">Will be blocked</p>
                              )}
                            </div>
                          </div>
                          {changed && (
                            <span className="text-xs text-yellow-400 uppercase tracking-wider">Changed</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  <div className="space-y-2 pt-2">
                    <Label className="text-zinc-300 text-xs uppercase tracking-wider">Reason (optional)</Label>
                    <Input
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      className="bg-zinc-950 border-zinc-800"
                      placeholder="e.g., Personal appointment"
                      data-testid="block-slot-reason-input"
                    />
                  </div>
                </>
              )}
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-2 pt-2">
            <Button
              variant="outline"
              onClick={closeDialog}
              disabled={saving}
              className="border-zinc-700 text-zinc-300"
              data-testid="cancel-block-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={applyChanges}
              disabled={saving}
              className={
                scope === 'full_day' && isFullyBlocked
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-red-700 hover:bg-red-600 text-white'
              }
              data-testid="confirm-block-toggle-btn"
            >
              {saving
                ? 'Saving…'
                : scope === 'full_day'
                ? isFullyBlocked
                  ? 'Unblock This Day'
                  : 'Block Full Day'
                : 'Apply Slot Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
